import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { GripVertical, Layers, Megaphone, Plus, Search, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { toast } from '@/components/ui/sonner'
import { cn } from '@/lib/utils'
import athleteApi from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { themeStyle } from '@/lib/themeStyle'
import { THEMES_GOAL, themesOf } from '@/lib/athleteScore'
import SetupShell from '@/components/setup/SetupShell'
import { CONTENT_CATEGORIES } from '@adthlete/common/utils/contentCategories'
import { CONTENT_THEMES, type ContentTheme } from '@adthlete/common/utils/dealPricing'
import { ThemeExamplePicker, ThemeExampleRow } from '@/components/setup/ThemeExamplePicker'

const fieldCls =
  'h-11 w-full rounded-xl border border-border bg-card px-3 text-base text-foreground placeholder:text-muted-foreground outline-none focus:border-[#84CC16] focus:ring-4 focus:ring-[#C0FD72]/40'

const Tile: React.FC<{ category?: string; size?: 'sm' | 'md' }> = ({ category, size = 'md' }) => {
  const s = themeStyle(category)
  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-xl',
        s.tile,
        size === 'sm' ? 'h-9 w-9' : 'h-11 w-11',
      )}
    >
      <s.Icon className={cn(s.icon, size === 'sm' ? 'h-4 w-4' : 'h-5 w-5')} />
    </span>
  )
}

/**
 * The athlete's top content themes: the formats they post again and again, ranked. Set from the
 * Content Shop and editable any time. Brands see them in this order on the scouting
 * report and book them at rate-card prices; the content calendar plans posts around them.
 */
const ContentThemes: React.FC = () => {
  const navigate = useNavigate()
  const { athlete, refreshUser } = useAuth()
  const saved = themesOf((athlete ?? {}) as any) as ContentTheme[]
  const themesDone = saved.length >= THEMES_GOAL
  const [themes, setThemes] = useState<ContentTheme[]>(saved)
  const [query, setQuery] = useState('')
  const [ownLabel, setOwnLabel] = useState('')
  const [ownCategory, setOwnCategory] = useState('')
  const [saving, setSaving] = useState(false)
  // The theme whose example post is being picked
  const [picking, setPicking] = useState<string | null>(null)
  const pickingTheme = themes.find((t) => t.id === picking) ?? null

  // Load once the athlete arrives (a reload lands here before /users/me returns)
  const loaded = useRef(saved.length > 0)
  useEffect(() => {
    if (!loaded.current && saved.length) {
      loaded.current = true
      setThemes(saved)
    }
  }, [saved])

  const full = themes.length >= THEMES_GOAL
  const has = (id: string) => themes.some((t) => t.id === id)
  const q = query.trim().toLowerCase()
  const catName = (slug: string) => CONTENT_CATEGORIES.find((c) => c.slug === slug)?.name ?? ''
  const suggestions = CONTENT_THEMES.filter(
    (t) =>
      !has(t.id) &&
      (!q || t.label.toLowerCase().includes(q) || catName(t.category).toLowerCase().includes(q)),
  )

  // ── Drag to rank (handle), or arrow keys on the handle ──────────────────────
  const listRef = useRef<HTMLOListElement>(null)
  const drag = useRef<{ index: number; startY: number; row: number } | null>(null)
  const [dragging, setDragging] = useState<number | null>(null)
  const move = (from: number, to: number) =>
    setThemes((list) => {
      if (to < 0 || to >= list.length || from === to) return list
      const next = [...list]
      const [item] = next.splice(from, 1)
      next.splice(to, 0, item)
      return next
    })
  const onPointerDown = (i: number) => (e: React.PointerEvent) => {
    const rowEl = listRef.current?.children[i] as HTMLElement | undefined
    const row = (rowEl?.getBoundingClientRect().height ?? 56) + 8
    drag.current = { index: i, startY: e.clientY, row }
    setDragging(i)
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    const d = drag.current
    if (!d) return
    const steps = Math.trunc((e.clientY - d.startY) / d.row)
    if (!steps) return
    const to = Math.max(0, Math.min(themes.length - 1, d.index + steps))
    if (to === d.index) return
    move(d.index, to)
    d.startY += (to - d.index) * d.row
    d.index = to
    setDragging(to)
  }
  const endDrag = () => {
    drag.current = null
    setDragging(null)
  }

  const addOwn = () => {
    const label = ownLabel.trim()
    if (!label || !ownCategory || full) return
    setThemes((list) => [
      ...list,
      {
        id: `custom:${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`,
        label,
        category: ownCategory,
      },
    ])
    setOwnLabel('')
    setOwnCategory('')
  }

  const save = async () => {
    if (!themes.length) {
      toast.error('Pick at least one theme', { description: 'What do you post most?' })
      return
    }
    setSaving(true)
    try {
      await athleteApi.put('/athlete/themes', { themes })
      await refreshUser()
      toast(
'Themes saved', {
        description: 'Brands see them in this order in your Content Shop.',
      })
      navigate('/brand-view')
    } catch (err: any) {
      toast.error(err?.response?.data?.error ?? "Couldn't save your themes")
    } finally {
      setSaving(false)
    }
  }

  return (
    <SetupShell
      title="Content themes"
      backTo="/brand-view"
      subtitle={
        <p className="text-sm text-muted-foreground">
          {`Pick the top ${THEMES_GOAL} recurring themes brands should know you for. They help with search, matching and rate-card requests.`}
        </p>
      }
      footer={
        <Button
          variant="lime"
          className="h-12 w-full text-base"
          disabled={saving || !themes.length}
          onClick={save}
        >
          {saving ? 'Saving…' : themesDone ? 'Save changes' : 'Save themes'}
        </Button>
      }
      footnote={
        <Link to="/brand-view" className="font-semibold underline">
          Preview my Content Shop
        </Link>
      }
    >
      {/* What to add, or what this list is for once it's done */}
      <div className="relative flex gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#E8F9D2]">
          <Layers className="h-6 w-6 text-foreground" />
        </span>
        <div className="min-w-0 pr-16">
          <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
            Your Content Shop
          </p>
          <p className="font-bold text-foreground">
            {themesDone
              ? `Your top ${THEMES_GOAL} ongoing series`
              : `Add your top ${THEMES_GOAL} content themes`}
          </p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {themesDone
              ? 'Rank the themes you post most consistently. Higher themes appear first in your Content Shop.'
              : 'The formats you post again and again. Brands book them at your rates and match you to campaigns with them.'}
          </p>
        </div>
        <span className="absolute right-3 top-3 rounded-full bg-[#E8F9D2] px-2.5 py-1 text-xs font-bold text-[#2F5E06]">
          Editable anytime
        </span>
      </div>

      {/* Their themes, ranked */}
      <div>
        <p className="font-bold text-foreground">
          Your themes ({themes.length}/{THEMES_GOAL})
        </p>
        <p className="mb-2 text-sm text-muted-foreground">
          {themes.length > 1
            ? 'Drag to rank them. Your most-posted theme goes first.'
            : 'Add the topics you post about most often.'}
        </p>
        {themes.length ? (
          <ol ref={listRef} className="space-y-2">
            {themes.map((t, i) => (
              <li
                key={t.id}
                className={cn(
                  'rounded-2xl border border-border bg-card p-2 pr-1 shadow-sm transition-shadow',
                  dragging === i && 'shadow-lg ring-2 ring-[#C0FD72]',
                )}
              >
                <div className="flex items-center gap-3">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-bold text-muted-foreground">
                    {i + 1}
                  </span>
                  <Tile category={t.category} />
                  <span className="min-w-0 flex-1 truncate font-semibold text-foreground">
                    {t.label}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${t.label}`}
                    onClick={() => setThemes((list) => list.filter((x) => x.id !== t.id))}
                    className="flex h-10 w-9 items-center justify-center text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    aria-label={`Move ${t.label} (rank ${i + 1}); use the arrow keys`}
                    onPointerDown={onPointerDown(i)}
                    onPointerMove={onPointerMove}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowUp') {
                        e.preventDefault()
                        move(i, i - 1)
                      } else if (e.key === 'ArrowDown') {
                        e.preventDefault()
                        move(i, i + 1)
                      }
                    }}
                    className="flex h-10 w-9 cursor-grab touch-none items-center justify-center text-muted-foreground active:cursor-grabbing"
                  >
                    <GripVertical className="h-5 w-5" />
                  </button>
                </div>
                {/* Proof for brands: one of their posts in this theme */}
                <div className="mt-2 pl-10 pr-2">
                  <ThemeExampleRow example={t.example} onOpen={() => setPicking(t.id)} />
                </div>
              </li>
            ))}
          </ol>
        ) : (
          <p className="rounded-2xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
            Nothing yet. Pick from the themes below.
          </p>
        )}
      </div>

      {/* Add or swap */}
      <div className="border-t border-border pt-4">
        <p className="font-bold text-foreground">
          {themes.length ? 'Swap or add themes' : 'Suggested themes'}
        </p>
        <p className="mb-2 text-sm text-muted-foreground">
          {full
            ? `You have ${THEMES_GOAL}. Remove one to swap in another.`
            : 'Add more if they also fit your content.'}
        </p>
        <div className="relative mb-3">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search all themes"
            aria-label="Search all themes"
            className={cn(fieldCls, 'pl-9')}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          {suggestions.map((t) => (
            <button
              key={t.id}
              type="button"
              disabled={full}
              onClick={() => setThemes((list) => [...list, t])}
              className="flex min-w-0 items-center gap-2 rounded-2xl border border-border bg-card p-2 text-left shadow-sm transition hover:border-[#84CC16] disabled:opacity-45"
            >
              <Tile category={t.category} size="sm" />
              <span className="min-w-0 flex-1 text-sm font-semibold leading-tight text-foreground">
                {t.label}
              </span>
              <Plus className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          ))}
        </div>
        {!suggestions.length ? (
          <p className="text-sm text-muted-foreground">
            No theme matches “{query}”. Add your own.
          </p>
        ) : null}

        {!full ? (
          <div className="mt-3 space-y-2 rounded-2xl border border-border bg-card p-3">
            <p className="text-sm font-semibold text-foreground">Something else? Add your own</p>
            <input
              value={ownLabel}
              onChange={(e) => setOwnLabel(e.target.value)}
              placeholder="e.g. Pregame playlist"
              aria-label="Your own theme"
              maxLength={40}
              className={fieldCls}
            />
            <div className="flex gap-2">
              <select
                value={ownCategory}
                onChange={(e) => setOwnCategory(e.target.value)}
                aria-label="What it's about"
                className={cn(fieldCls, 'min-w-0 flex-1')}
              >
                <option value="">What it's about</option>
                {CONTENT_CATEGORIES.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.name}
                  </option>
                ))}
              </select>
              <Button
                type="button"
                variant="outline"
                className="h-11 shrink-0"
                disabled={!ownLabel.trim() || !ownCategory}
                onClick={addOwn}
              >
                Add
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <ThemeExamplePicker
        open={!!pickingTheme}
        onOpenChange={(o) => !o && setPicking(null)}
        themeLabel={pickingTheme?.label ?? ''}
        current={pickingTheme?.example}
        onPick={(example) =>
          setThemes((list) => list.map((x) => (x.id === picking ? { ...x, example } : x)))
        }
      />

      <div className="flex gap-3 rounded-2xl border border-[#C7E2FB] bg-[#F3F9FF] p-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#E0F0FF]">
          <Megaphone className="h-5 w-5 text-[#1D72D8]" />
        </span>
        <div>
          <p className="font-bold text-foreground">How brands use this</p>
          <p className="text-sm text-muted-foreground">
            Your themes are the series in your Content Shop: brands book them at your rates, and
            they explain why you fit a campaign. An example post under each one shows brands you
            really make it. Your content calendar suggests them too.
          </p>
        </div>
      </div>
    </SetupShell>
  )
}

export default ContentThemes
